import { Box, Flex, HStack, IconButton, Text, VStack } from "@chakra-ui/react";
import { useRemoteParticipants } from "@livekit/components-react";
import { RoomEvent } from "livekit-client";
import React, { useCallback, useMemo } from "react";
import colors from "../../../app/colors";
import UserAvatar from "../../_shared/user-avatar";
import { getUserFullName } from "../../utils/Functions";
import { useUsers } from "../users-provider";
import {
  CloseFullscreenIcon,
  GroupsOutlinedIcon,
  PersonAddAltOutlinedIcon,
} from "./video-call-bar-icons";

const participantRoomEvents = [
  RoomEvent.ParticipantConnected,
  RoomEvent.ParticipantDisconnected,
];

export type VideoCallAppBarProps = {
  /** Shown until at least one other participant is connected (e.g. caller name while ringing). */
  fallbackTitle: string;
  durationLabel?: string;
  /**
   * `Call.activeUserIdList.length` from the server. Only maintained by LiveKit webhooks, so
   * it can lag or stay empty; used as a floor for the badge, never as the sole source.
   */
  serverParticipantCount: number;
  onOpenAddParticipants: () => void;
  onOpenCallParticipants: (activeUserIdList: string[]) => void;
  onMinimize: () => void;
};

/**
 * Call header. Title and participant count come from the live LiveKit room (matches
 * mobile): one other person shows their full name and avatar, several show first names.
 */
export const VideoCallTopBar: React.FC<VideoCallAppBarProps> = ({
  fallbackTitle,
  durationLabel,
  serverParticipantCount,
  onOpenAddParticipants,
  onOpenCallParticipants,
  onMinimize,
}) => {
  const remoteParticipants = useRemoteParticipants({
    updateOnlyOn: participantRoomEvents,
  });
  const { usersById } = useUsers();

  const remoteUsers = useMemo(
    () =>
      remoteParticipants
        .map((p) => usersById[p.identity])
        .filter((u): u is NonNullable<typeof u> => u != null),
    [remoteParticipants, usersById],
  );

  const title = useMemo(() => {
    if (remoteUsers.length === 1) {
      return getUserFullName(remoteUsers[0]);
    }
    if (remoteUsers.length > 1) {
      return remoteUsers.map((u) => u.firstName).join(", ");
    }
    return fallbackTitle;
  }, [fallbackTitle, remoteUsers]);

  const avatarUserId =
    remoteUsers.length === 1 ? remoteUsers[0].id : undefined;

  // Once connected, the LiveKit room is the source of truth (remote participants + me) and
  // updates on ParticipantConnected/Disconnected. The server count is a floor for the moment
  // before the room connects; the badge never shows 0.
  const roomParticipantCount = remoteParticipants.length + 1;
  const connectedCount = Math.max(
    serverParticipantCount,
    roomParticipantCount,
    1,
  );

  const handleOpenCallParticipants = useCallback(() => {
    onOpenCallParticipants(remoteParticipants.map((p) => p.identity));
  }, [onOpenCallParticipants, remoteParticipants]);

  return (
    <Flex
      className="lk-call-top-bar"
      w="full"
      align="center"
      justify="space-between"
      flexShrink={0}
      px={2}
      pt="max(8px, env(safe-area-inset-top))"
      pb={2}
    >
      <HStack spacing={2} align="center" minW={0} flex="1">
        <Box w="8px" flexShrink={0} />
        {avatarUserId ? (
          <Box pr={2} flexShrink={0}>
            <UserAvatar
              userId={avatarUserId}
              size="sm"
              displayOnlineIndicator={false}
            />
          </Box>
        ) : null}
        <VStack align="start" spacing={0} minW={0}>
          <Text
            fontFamily="Outfit"
            fontSize="18px"
            lineHeight="22px"
            fontWeight={700}
            color={colors.white}
            noOfLines={1}
          >
            {title}
          </Text>
          {durationLabel ? (
            <Text
              fontSize="14px"
              lineHeight="16px"
              fontWeight={700}
              color={colors.gray30}
            >
              {durationLabel}
            </Text>
          ) : null}
        </VStack>
      </HStack>
      <HStack spacing="24px" flexShrink={0}>
        <IconButton
          variant="ghost"
          aria-label="Add participants"
          title="Add participants"
          icon={<PersonAddAltOutlinedIcon size={24} />}
          color={colors.white}
          onClick={onOpenAddParticipants}
          _hover={{ bg: "rgba(255,255,255,0.08)" }}
        />
        <IconButton
          variant="ghost"
          aria-label="Call participants"
          title="Call participants"
          onClick={handleOpenCallParticipants}
          color={colors.white}
          _hover={{ bg: "rgba(255,255,255,0.08)" }}
          icon={
            <HStack spacing={1}>
              <GroupsOutlinedIcon size={24} />
              <Text
                as="span"
                fontSize="14px"
                fontWeight={700}
                color={colors.gray30}
              >
                {connectedCount}
              </Text>
            </HStack>
          }
        />
        <IconButton
          variant="ghost"
          aria-label="Use the app during this call"
          title="Use the app during this call"
          icon={<CloseFullscreenIcon size={24} />}
          color={colors.white}
          onClick={onMinimize}
          _hover={{ bg: "rgba(255,255,255,0.08)" }}
        />
      </HStack>
    </Flex>
  );
};
